import React, { useState, useEffect } from 'react';
import { Card, Table, Pagination, Form, Button, Row, Col, InputGroup, Dropdown, DropdownButton, Nav, Accordion, Image, Modal } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { database, storage } from '../firebase';
import { ref, onValue, push, remove, set, get } from 'firebase/database';
import { ref as storageRef, deleteObject, listAll } from 'firebase/storage';
import { Bike } from '../types/bike';
import { Link, useLocation } from 'react-router-dom';
import FileUploader from './FileUploader';

interface EditableBike extends Bike {
  id: string;
  isEditing?: boolean;
  isExpanded?: boolean;
}

const Bikes = () => {
  const { currentUser } = useAuth();
  const [bikes, setBikes] = useState<EditableBike[]>([]);
  const [ownedBikeIds, setOwnedBikeIds] = useState<string[]>([]);
  const [valveTypes, setValveTypes] = useState<{[key: string]: string}>({});
  const [showAddValveTypeModal, setShowAddValveTypeModal] = useState(false);
  const [newValveTypeInput, setNewValveTypeInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showIds, setShowIds] = useState(false);
  const itemsPerPage = 20;
  const [newBike, setNewBike] = useState<Omit<Bike, 'id'>>({
    brand: '',
    model: '',
    year: '',
    color: '',
    frameNumber: '',
    wheelSize: '',
    tire: '',
    tube: '',
    valveType: '',
    valveLength: ''
  });
  
  // New state variables for search and filtering
  const [searchTerm, setSearchTerm] = useState('');
  const [filterField, setFilterField] = useState<keyof Omit<Bike, 'id'> | 'all'>('all');
  const [sortField, setSortField] = useState<keyof Omit<Bike, 'id'> | 'none'>('none');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [uniqueBrands, setUniqueBrands] = useState<string[]>([]);
  const [uniqueColors, setUniqueColors] = useState<string[]>([]);
  const [uniqueWheelSizes, setUniqueWheelSizes] = useState<string[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [selectedWheelSize, setSelectedWheelSize] = useState<string>('');
  const location = useLocation();

  useEffect(() => {
    if (!currentUser) return;

    // Fetch all bikes
    const bikesRef = ref(database, 'bikes');
    const bikesUnsubscribe = onValue(bikesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const bikesArray = Object.entries(data).map(([id, bike]) => ({
          id,
          ...(bike as Omit<Bike, 'id'>),
          isEditing: false
        }));
        setBikes(bikesArray);
        
        // Extract unique brands, colors, and wheel sizes for filtering
        const brands = Array.from(new Set(bikesArray.map(bike => bike.brand)));
        const colors = Array.from(new Set(bikesArray.map(bike => bike.color)));
        const wheelSizes = Array.from(new Set(bikesArray
          .filter(bike => bike.wheelSize) // Filter out undefined wheel sizes
          .map(bike => bike.wheelSize as string)
        ));
        setUniqueBrands(brands);
        setUniqueColors(colors);
        setUniqueWheelSizes(wheelSizes);
      } else {
        setBikes([]);
        setUniqueBrands([]);
        setUniqueColors([]);
        setUniqueWheelSizes([]);
      }
    });

    // Fetch owned bikes
    const ownedBikesRef = ref(database, `user_settings/${currentUser.uid}/ownedBikes`);
    const ownedBikesUnsubscribe = onValue(ownedBikesRef, (snapshot) => {
      const data = snapshot.val() || {};
      setOwnedBikeIds(Object.values(data));
    });

    // Fetch valve types
    const valveTypesRef = ref(database, `user_settings/${currentUser.uid}/valve_types`);
    const valveTypesUnsubscribe = onValue(valveTypesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setValveTypes(data);
      } else {
        setValveTypes({});
      }
    });

    return () => {
      bikesUnsubscribe();
      ownedBikesUnsubscribe();
      valveTypesUnsubscribe();
    };
  }, [currentUser]);

  const handleInputChange = (field: keyof Omit<Bike, 'id'>, value: string) => {
    if (field === 'valveType' && value === 'ADD_NEW') {
      setShowAddValveTypeModal(true);
      return;
    }
    setNewBike(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddNewValveType = async () => {
    if (!currentUser || !newValveTypeInput.trim()) return;

    try {
      const valveTypesRef = ref(database, `user_settings/${currentUser.uid}/valve_types`);
      const newValveTypeRef = await push(valveTypesRef, newValveTypeInput.trim());
      
      // Set the newly added valve type as selected
      setNewBike(prev => ({
        ...prev,
        valveType: newValveTypeInput.trim()
      }));
      
      setNewValveTypeInput('');
      setShowAddValveTypeModal(false);
    } catch (error) {
      console.error('Error adding valve type:', error);
    }
  };

  const handleEdit = (bikeId: string) => {
    setBikes(bikes.map(bike => 
      bike.id === bikeId ? { ...bike, isEditing: true } : bike
    ));
  };

  const handleCancelEdit = (bikeId: string) => {
    setBikes(bikes.map(bike => 
      bike.id === bikeId ? { ...bike, isEditing: false } : bike
    ));
  };

  const handleEditInputChange = (bikeId: string, field: keyof Omit<Bike, 'id'>, value: string) => {
    if (field === 'valveType' && value === 'ADD_NEW') {
      setShowAddValveTypeModal(true);
      return;
    }
    setBikes(bikes.map(bike => 
      bike.id === bikeId ? { ...bike, [field]: value } : bike
    ));
  };

  const toggleExpandBike = (bikeId: string) => {
    setBikes(bikes.map(bike => 
      bike.id === bikeId ? { ...bike, isExpanded: !bike.isExpanded } : bike
    ));
  };

  const handleBikePictureUpload = async (bikeId: string, url: string) => {
    try {
      const bikeRef = ref(database, `bikes/${bikeId}`);
      const bike = bikes.find(b => b.id === bikeId);
      if (!bike) return;

      const { id, isEditing, isExpanded, ...bikeData } = bike;
      await set(bikeRef, { ...bikeData, bikePictureUrl: url });
      
      // Update local state
      setBikes(bikes.map(b => 
        b.id === bikeId ? { ...b, bikePictureUrl: url } : b
      ));
    } catch (error: unknown) {
      console.error('Error updating bike picture:', error);
    }
  };

  const handleBillUpload = async (bikeId: string, url: string) => {
    try {
      const bikeRef = ref(database, `bikes/${bikeId}`);
      const bike = bikes.find(b => b.id === bikeId);
      if (!bike) return;

      const { id, isEditing, isExpanded, ...bikeData } = bike;
      await set(bikeRef, { ...bikeData, billUrl: url });
      
      // Update local state
      setBikes(bikes.map(b => 
        b.id === bikeId ? { ...b, billUrl: url } : b
      ));
    } catch (error: unknown) {
      console.error('Error updating bill:', error);
    }
  };

  const handleBikePictureDelete = async (bikeId: string) => {
    try {
      const bikeRef = ref(database, `bikes/${bikeId}`);
      const bike = bikes.find(b => b.id === bikeId);
      if (!bike) return;

      const { id, isEditing, isExpanded, ...bikeData } = bike;
      const { bikePictureUrl, ...restBikeData } = bikeData;
      await set(bikeRef, restBikeData);
      
      // Update local state
      setBikes(bikes.map(b => 
        b.id === bikeId ? { ...b, bikePictureUrl: undefined } : b
      ));
    } catch (error: unknown) {
      console.error('Error deleting bike picture:', error);
    }
  };

  const handleBillDelete = async (bikeId: string) => {
    try {
      const bikeRef = ref(database, `bikes/${bikeId}`);
      const bike = bikes.find(b => b.id === bikeId);
      if (!bike) return;

      const { id, isEditing, isExpanded, ...bikeData } = bike;
      const { billUrl, ...restBikeData } = bikeData;
      await set(bikeRef, restBikeData);
      
      // Update local state
      setBikes(bikes.map(b => 
        b.id === bikeId ? { ...b, billUrl: undefined } : b
      ));
    } catch (error: unknown) {
      console.error('Error deleting bill:', error);
    }
  };

  const handleSaveEdit = async (bikeId: string) => {
    const bike = bikes.find(b => b.id === bikeId);
    if (!bike) return;

    try {
      const bikeRef = ref(database, `bikes/${bikeId}`);
      const { id, isEditing, isExpanded, ...bikeData } = bike;
      await set(bikeRef, bikeData);
      
      setBikes(bikes.map(b => 
        b.id === bikeId ? { ...b, isEditing: false } : b
      ));
    } catch (error: unknown) {
      console.error('Error updating bike:', error);
    }
  };

  const deleteFilesFromStorage = async (userId: string, bikeId: string) => {
    try {
      // Create reference to the bike folder in storage
      const bikeStorageRef = storageRef(storage, `bikes/${userId}/${bikeId}`);
      
      // List all files in the folder
      const result = await listAll(bikeStorageRef);
      
      // Delete all files and folders
      const deletePromises = [];
      
      // Delete all files in the root
      for (const item of result.items) {
        deletePromises.push(deleteObject(item));
      }
      
      // Delete files in subfolders (bikePicture and bill folders)
      for (const folder of result.prefixes) {
        const folderContents = await listAll(folder);
        for (const item of folderContents.items) {
          deletePromises.push(deleteObject(item));
        }
      }
      
      // Wait for all deletions to complete
      if (deletePromises.length > 0) {
        await Promise.all(deletePromises);
        console.log(`Deleted all files for bike ${bikeId}`);
      }
    } catch (error: unknown) {
      // If error is because there are no files, that's fine
      if (error && typeof error === 'object' && 'code' in error && error.code === 'storage/object-not-found') {
        console.log(`No files found for bike ${bikeId}`);
      } else {
        console.error('Error deleting files from storage:', error);
      }
    }
  };

  const handleRemoveBike = async (bikeId: string) => {
    if (!currentUser || !window.confirm('Sind Sie sicher, dass Sie dieses Velo löschen möchten?')) return;

    try {
      // First check if other users own this bike
      const allUsersRef = ref(database, 'user_settings');
      const allUsersSnapshot = await get(allUsersRef);
      const allUsers = allUsersSnapshot.val() || {};
      
      let otherOwnersExist = false;
      for (const userId in allUsers) {
        if (userId === currentUser.uid) continue;
        const userOwnedBikes = allUsers[userId]?.ownedBikes || {};
        if (Object.values(userOwnedBikes).includes(bikeId)) {
          otherOwnersExist = true;
          break;
        }
      }

      // Remove from user's owned bikes
      const updatedOwnedBikes = ownedBikeIds.filter(id => id !== bikeId);
      const ownedBikesRef = ref(database, `user_settings/${currentUser.uid}/ownedBikes`);
      await set(ownedBikesRef, updatedOwnedBikes);

      // Delete any files associated with this bike for this user
      await deleteFilesFromStorage(currentUser.uid, bikeId);

      // Only delete bike from global collection if no other users own it
      if (!otherOwnersExist) {
        const bikeRef = ref(database, `bikes/${bikeId}`);
        await remove(bikeRef);
      }
    } catch (error: unknown) {
      console.error('Error removing bike:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    try {
      const bikesRef = ref(database, 'bikes');
      const newBikeRef = await push(bikesRef, newBike);
      
      // Add the new bike to user's owned bikes
      const ownedBikesRef = ref(database, `user_settings/${currentUser.uid}/ownedBikes`);
      const updatedOwnedBikes = [...ownedBikeIds, newBikeRef.key];
      await set(ownedBikesRef, updatedOwnedBikes);
      
      // Reset form
      setNewBike({
        brand: '',
        model: '',
        year: '',
        color: '',
        frameNumber: '',
        wheelSize: '',
        tire: '',
        tube: '',
        valveType: '',
        valveLength: ''
      });
    } catch (error: unknown) {
      console.error('Error adding bike:', error);
    }
  };

  const handleToggleOwned = async (bikeId: string, isCurrentlyOwned: boolean) => {
    if (!currentUser) return;

    try {
      const ownedBikesRef = ref(database, `user_settings/${currentUser.uid}/ownedBikes`);
      
      if (isCurrentlyOwned) {
        // Remove bike from owned list
        const updatedOwnedBikes = ownedBikeIds.filter(id => id !== bikeId);
        await set(ownedBikesRef, updatedOwnedBikes);
      } else {
        // Add bike to owned list
        const updatedOwnedBikes = [...ownedBikeIds, bikeId];
        await set(ownedBikesRef, updatedOwnedBikes);
      }
    } catch (error: unknown) {
      console.error('Error updating owned bikes:', error);
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setFilterField('all');
    setSortField('none');
    setSortDirection('asc');
    setSelectedBrand('');
    setSelectedColor('');
    setSelectedWheelSize('');
    setCurrentPage(1);
  };

  // Apply search and filters to bikes
  const applyFiltersAndSearch = (bikes: EditableBike[]) => {
    let filteredResults = bikes.filter(bike => ownedBikeIds.includes(bike.id));
    
    // Apply brand filter
    if (selectedBrand) {
      filteredResults = filteredResults.filter(bike => bike.brand === selectedBrand);
    }
    
    // Apply color filter
    if (selectedColor) {
      filteredResults = filteredResults.filter(bike => bike.color === selectedColor);
    }
    
    // Apply wheel size filter
    if (selectedWheelSize) {
      filteredResults = filteredResults.filter(bike => bike.wheelSize && bike.wheelSize === selectedWheelSize);
    }
    
    // Apply search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredResults = filteredResults.filter(bike => {
        if (filterField === 'all') {
          return (
            bike.brand.toLowerCase().includes(searchLower) ||
            bike.model.toLowerCase().includes(searchLower) ||
            bike.year.toLowerCase().includes(searchLower) ||
            bike.color.toLowerCase().includes(searchLower) ||
            bike.frameNumber.toLowerCase().includes(searchLower) ||
            (bike.wheelSize ? bike.wheelSize.toLowerCase().includes(searchLower) : false) ||
            (bike.tire ? bike.tire.toLowerCase().includes(searchLower) : false) ||
            (bike.tube ? bike.tube.toLowerCase().includes(searchLower) : false) ||
            (bike.valveType ? bike.valveType.toLowerCase().includes(searchLower) : false) ||
            (bike.valveLength ? bike.valveLength.toLowerCase().includes(searchLower) : false)
          );
        } else {
          if (filterField === 'wheelSize' && !bike.wheelSize) {
            return false;
          }
          return String(bike[filterField] || '').toLowerCase().includes(searchLower);
        }
      });
    }
    
    // Apply sorting
    if (sortField !== 'none') {
      filteredResults.sort((a, b) => {
        const valueA = (a[sortField] || '').toLowerCase();
        const valueB = (b[sortField] || '').toLowerCase();
        
        if (sortDirection === 'asc') {
          return valueA.localeCompare(valueB);
        } else {
          return valueB.localeCompare(valueA);
        }
      });
    }
    
    return filteredResults;
  };

  // Pagination calculations with filters applied
  const filteredBikes = applyFiltersAndSearch(bikes);
  const indexOfLastBike = currentPage * itemsPerPage;
  const indexOfFirstBike = indexOfLastBike - itemsPerPage;
  const currentBikes = filteredBikes.slice(indexOfFirstBike, indexOfLastBike);
  const totalPages = Math.ceil(filteredBikes.length / itemsPerPage);

  // Generate pagination items
  const paginationItems = [];
  for (let number = 1; number <= totalPages; number++) {
    paginationItems.push(
      <Pagination.Item 
        key={number} 
        active={number === currentPage}
        onClick={() => setCurrentPage(number)}
      >
        {number}
      </Pagination.Item>
    );
  }

  if (!currentUser) {
    return (
      <Card>
        <Card.Body>
          <Card.Title style={{ color: '#1780a6', fontSize: '2rem', marginBottom: '1rem' }}>VeGaVelos</Card.Title>
          {/* Bike section tabs, shown only on /bike, /bike/maintenance, /bike/settings */}
          {['/bike', '/bike/maintenance', '/bike/settings'].includes(location.pathname) && (
            <Nav variant="tabs" className="mb-4">
              <Nav.Item>
                <Nav.Link as={Link} to="/bike" active={location.pathname === '/bike'}>
                  Velos
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link as={Link} to="/bike/maintenance" active={location.pathname === '/bike/maintenance'}>
                  Wartung
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link as={Link} to="/bike/settings" active={location.pathname === '/bike/settings'}>
                  Konfiguration
                </Nav.Link>
              </Nav.Item>
            </Nav>
          )}
          <p>Please log in to view bikes.</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="container">
      <h2 className="mb-4">Velos</h2>
      {/* Bike section tabs, shown only on /bike, /bike/maintenance, /bike/settings */}
      {['/bike', '/bike/maintenance', '/bike/settings'].includes(location.pathname) && (
        <Nav variant="tabs" className="mb-4">
          <Nav.Item>
            <Nav.Link as={Link} to="/bike" active={location.pathname === '/bike'}>
              Velos
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as={Link} to="/bike/maintenance" active={location.pathname === '/bike/maintenance'}>
              Wartung
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as={Link} to="/bike/settings" active={location.pathname === '/bike/settings'}>
              Konfiguration
            </Nav.Link>
          </Nav.Item>
        </Nav>
      )}

      <Card className="mb-4">
        <Card.Body>
          <Card.Title style={{ color: '#1780a6' }}>Velo erfassen</Card.Title>
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Marke</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={newBike.brand}
                    onChange={(e) => handleInputChange('brand', e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Modell</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={newBike.model}
                    onChange={(e) => handleInputChange('model', e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Jahr</Form.Label>
                  <Form.Control 
                    type="number" 
                    value={newBike.year}
                    onChange={(e) => handleInputChange('year', e.target.value)}
                    min="2000"
                    max="2100"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Farbe</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={newBike.color}
                    onChange={(e) => handleInputChange('color', e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Rahmennummer</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={newBike.frameNumber}
                    onChange={(e) => handleInputChange('frameNumber', e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Radgrösse</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={newBike.wheelSize}
                    onChange={(e) => handleInputChange('wheelSize', e.target.value)}
                    placeholder="z.B. 28 Zoll"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Bereifung</Form.Label>
                  <Form.Control
                    type="text"
                    value={newBike.tire || ''}
                    onChange={(e) => handleInputChange('tire', e.target.value)}
                    placeholder="z.B. 700x28C"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Schlauch</Form.Label>
                  <Form.Control
                    type="text"
                    value={newBike.tube || ''}
                    onChange={(e) => handleInputChange('tube', e.target.value)}
                    placeholder="z.B. 28-622"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Ventil-Art</Form.Label>
                  <Form.Select
                    value={newBike.valveType || ''}
                    onChange={(e) => handleInputChange('valveType', e.target.value)}
                  >
                    <option value="">Ventil-Art auswählen</option>
                    {Object.entries(valveTypes).map(([id, type]) => (
                      <option key={id} value={type}>{type}</option>
                    ))}
                    {Object.keys(valveTypes).length === 0 && (
                      <option value="ADD_NEW">+ Neue Ventil-Art hinzufügen</option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Ventil Länge</Form.Label>
                  <Form.Control
                    type="text"
                    value={newBike.valveLength || ''}
                    onChange={(e) => handleInputChange('valveLength', e.target.value)}
                    placeholder="z.B. 48 mm"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Button type="submit" variant="primary">
              Speichern
            </Button>
          </Form>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center">
            <Card.Title style={{ color: '#1780a6' }}>Velos</Card.Title>
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={() => setShowIds(!showIds)}
              className="mb-2"
            >
              {showIds ? 'IDs ausblenden' : 'IDs anzeigen'}
            </Button>
          </div>
          
          {/* Search and Filter Controls */}
          <Row className="mb-3">
            <Col md={6}>
              <InputGroup>
                <Form.Control
                  placeholder="Suchen..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to first page when search changes
                  }}
                />
                <DropdownButton
                  variant="outline-secondary"
                  title={`Suchen in: ${filterField === 'all' ? 'Alle Felder' : 
                          filterField === 'brand' ? 'Marke' : 
                          filterField === 'model' ? 'Modell' : 
                          filterField === 'year' ? 'Jahr' : 
                          filterField === 'color' ? 'Farbe' : 
                          filterField === 'frameNumber' ? 'Rahmennummer' : 
                          filterField === 'wheelSize' ? 'Radgrösse' : 
                          filterField === 'tire' ? 'Bereifung' : 
                          filterField === 'tube' ? 'Schlauch' : 
                          filterField === 'valveType' ? 'Ventil-Art' : 
                          filterField === 'valveLength' ? 'Ventil Länge' : ''}`}
                  id="search-field-dropdown"
                >
                  <Dropdown.Item onClick={() => setFilterField('all')}>Alle Felder</Dropdown.Item>
                  <Dropdown.Item onClick={() => setFilterField('brand')}>Marke</Dropdown.Item>
                  <Dropdown.Item onClick={() => setFilterField('model')}>Modell</Dropdown.Item>
                  <Dropdown.Item onClick={() => setFilterField('year')}>Jahr</Dropdown.Item>
                  <Dropdown.Item onClick={() => setFilterField('color')}>Farbe</Dropdown.Item>
                  <Dropdown.Item onClick={() => setFilterField('frameNumber')}>Rahmennummer</Dropdown.Item>
                  <Dropdown.Item onClick={() => setFilterField('wheelSize')}>Radgrösse</Dropdown.Item>
                  <Dropdown.Item onClick={() => setFilterField('tire')}>Bereifung</Dropdown.Item>
                  <Dropdown.Item onClick={() => setFilterField('tube')}>Schlauch</Dropdown.Item>
                  <Dropdown.Item onClick={() => setFilterField('valveType')}>Ventil-Art</Dropdown.Item>
                  <Dropdown.Item onClick={() => setFilterField('valveLength')}>Ventil Länge</Dropdown.Item>
                </DropdownButton>
              </InputGroup>
            </Col>
            <Col md={6}>
              <Row>
                <Col md={4}>
                  <Form.Group>
                    <Form.Select 
                      value={selectedBrand} 
                      onChange={(e) => {
                        setSelectedBrand(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">Alle Marken</option>
                      {uniqueBrands.map(brand => (
                        <option key={brand} value={brand}>{brand}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Select 
                      value={selectedColor} 
                      onChange={(e) => {
                        setSelectedColor(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">Alle Farben</option>
                      {uniqueColors.map(color => (
                        <option key={color} value={color}>{color}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Select 
                      value={selectedWheelSize} 
                      onChange={(e) => {
                        setSelectedWheelSize(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">Alle Radgrössen</option>
                      {uniqueWheelSizes.map(wheelSize => (
                        <option key={wheelSize} value={wheelSize}>{wheelSize}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Col>
          </Row>
          
          <Row className="mb-3">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>Sortieren nach</InputGroup.Text>
                <Form.Select
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value as keyof Omit<Bike, 'id'> | 'none')}
                >
                  <option value="none">Keine Sortierung</option>
                  <option value="brand">Marke</option>
                  <option value="model">Modell</option>
                  <option value="year">Jahr</option>
                  <option value="color">Farbe</option>
                  <option value="frameNumber">Rahmennummer</option>
                  <option value="wheelSize">Radgrösse</option>
                </Form.Select>
                <Button 
                  variant="outline-secondary"
                  onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                >
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </Button>
              </InputGroup>
            </Col>
            <Col md={6} className="d-flex justify-content-end">
              <Button 
                variant="outline-secondary" 
                onClick={resetFilters}
              >
                Filter zurücksetzen
              </Button>
              <div className="ms-2">
                <span className="badge bg-info">
                  {filteredBikes.length} Velos gefunden
                </span>
              </div>
            </Col>
          </Row>
          
          {filteredBikes.length > 0 ? (
            <>
              <div className="table-responsive">
                <Table striped bordered hover className="mt-3">
                  <thead>
                    <tr>
                      {showIds && <th>ID</th>}
                      <th>Marke</th>
                      <th>Modell</th>
                      <th>Jahr</th>
                      <th>Farbe</th>
                      <th>Rahmennummer</th>
                      <th>Radgrösse</th>
                      <th>Bereifung</th>
                      <th>Schlauch</th>
                      <th>Ventil-Art</th>
                      <th>Ventil Länge</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentBikes.map(bike => [
                      <tr key={bike.id}>
                        {showIds && <td>{bike.id}</td>}
                        <td>
                          {bike.isEditing ? (
                            <Form.Control
                              type="text"
                              value={bike.brand}
                              onChange={(e) => handleEditInputChange(bike.id, 'brand', e.target.value)}
                              required
                            />
                          ) : (
                            bike.brand
                          )}
                        </td>
                        <td>
                          {bike.isEditing ? (
                            <Form.Control
                              type="text"
                              value={bike.model}
                              onChange={(e) => handleEditInputChange(bike.id, 'model', e.target.value)}
                              required
                            />
                          ) : (
                            bike.model
                          )}
                        </td>
                        <td>
                          {bike.isEditing ? (
                            <Form.Control
                              type="number"
                              value={bike.year}
                              onChange={(e) => handleEditInputChange(bike.id, 'year', e.target.value)}
                              min="2000"
                              max="2100"
                              required
                            />
                          ) : (
                            bike.year
                          )}
                        </td>
                        <td>
                          {bike.isEditing ? (
                            <Form.Control
                              type="text"
                              value={bike.color}
                              onChange={(e) => handleEditInputChange(bike.id, 'color', e.target.value)}
                              required
                            />
                          ) : (
                            bike.color
                          )}
                        </td>
                        <td>
                          {bike.isEditing ? (
                            <Form.Control
                              type="text"
                              value={bike.frameNumber}
                              onChange={(e) => handleEditInputChange(bike.id, 'frameNumber', e.target.value)}
                              required
                            />
                          ) : (
                            bike.frameNumber
                          )}
                        </td>
                        <td>
                          {bike.isEditing ? (
                            <Form.Control
                              type="text"
                              value={bike.wheelSize || ''}
                              onChange={(e) => handleEditInputChange(bike.id, 'wheelSize', e.target.value)}
                            />
                          ) : (
                            bike.wheelSize || '-'
                          )}
                        </td>
                        <td>
                          {bike.isEditing ? (
                            <Form.Control
                              type="text"
                              value={bike.tire || ''}
                              onChange={(e) => handleEditInputChange(bike.id, 'tire', e.target.value)}
                            />
                          ) : (
                            bike.tire || '-'
                          )}
                        </td>
                        <td>
                          {bike.isEditing ? (
                            <Form.Control
                              type="text"
                              value={bike.tube || ''}
                              onChange={(e) => handleEditInputChange(bike.id, 'tube', e.target.value)}
                            />
                          ) : (
                            bike.tube || '-'
                          )}
                        </td>
                        <td>
                          {bike.isEditing ? (
                            <Form.Select
                              value={bike.valveType || ''}
                              onChange={(e) => handleEditInputChange(bike.id, 'valveType', e.target.value)}
                            >
                              <option value="">Ventil-Art auswählen</option>
                              {Object.entries(valveTypes).map(([id, type]) => (
                                <option key={id} value={type}>{type}</option>
                              ))}
                              {Object.keys(valveTypes).length === 0 && (
                                <option value="ADD_NEW">+ Neue Ventil-Art hinzufügen</option>
                              )}
                            </Form.Select>
                          ) : (
                            bike.valveType || '-'
                          )}
                        </td>
                        <td>
                          {bike.isEditing ? (
                            <Form.Control
                              type="text"
                              value={bike.valveLength || ''}
                              onChange={(e) => handleEditInputChange(bike.id, 'valveLength', e.target.value)}
                            />
                          ) : (
                            bike.valveLength || '-'
                          )}
                        </td>
                        <td>
                          {bike.isEditing ? (
                            <>
                              <Button 
                                variant="success" 
                                size="sm"
                                className="me-2"
                                onClick={() => handleSaveEdit(bike.id)}
                              >
                                Speichern
                              </Button>
                              <Button 
                                variant="secondary" 
                                size="sm"
                                onClick={() => handleCancelEdit(bike.id)}
                              >
                                Abbrechen
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button 
                                variant="primary" 
                                size="sm"
                                className="me-2"
                                onClick={() => handleEdit(bike.id)}
                              >
                                Bearbeiten
                              </Button>
                              <Button 
                                variant="info" 
                                size="sm"
                                className="me-2"
                                onClick={() => toggleExpandBike(bike.id)}
                              >
                                {bike.isExpanded ? 'Zuklappen' : 'Details'}
                              </Button>
                              <Button 
                                variant="danger" 
                                size="sm"
                                onClick={() => handleRemoveBike(bike.id)}
                              >
                                Löschen
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>,
                      bike.isExpanded && (
                        <tr key={`${bike.id}-expanded`}>
                          <td colSpan={showIds ? 8 : 7}>
                            <div className="p-3">
                              <h5 className="mb-3">Detailansicht: {bike.brand} {bike.model}</h5>
                              <Row>
                                <Col md={6}>
                                  <FileUploader
                                    fileType="bikePicture"
                                    bikeId={bike.id}
                                    existingUrl={bike.bikePictureUrl}
                                    onUploadComplete={(url) => handleBikePictureUpload(bike.id, url)}
                                    onDelete={() => handleBikePictureDelete(bike.id)}
                                  />
                                </Col>
                                <Col md={6}>
                                  <FileUploader
                                    fileType="bill"
                                    bikeId={bike.id}
                                    existingUrl={bike.billUrl}
                                    onUploadComplete={(url) => handleBillUpload(bike.id, url)}
                                    onDelete={() => handleBillDelete(bike.id)}
                                  />
                                </Col>
                              </Row>
                            </div>
                          </td>
                        </tr>
                      )
                    ]).flat().filter(Boolean)}
                  </tbody>
                </Table>
              </div>
              <div className="d-flex justify-content-center mt-3">
                <Pagination>
                  <Pagination.First 
                    onClick={() => setCurrentPage(1)} 
                    disabled={currentPage === 1}
                  />
                  <Pagination.Prev 
                    onClick={() => setCurrentPage(curr => Math.max(curr - 1, 1))}
                    disabled={currentPage === 1}
                  />
                  {paginationItems}
                  <Pagination.Next 
                    onClick={() => setCurrentPage(curr => Math.min(curr + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  />
                  <Pagination.Last 
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            </>
          ) : (
            <div className="text-muted text-center mt-3">
              {bikes.length > 0 ? 'Keine Velos entsprechen den Filterkriterien.' : 'Keine Velos vorhanden.'}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add Valve Type Modal */}
      <Modal show={showAddValveTypeModal} onHide={() => setShowAddValveTypeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Neue Ventil-Art hinzufügen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Ventil-Art Name</Form.Label>
              <Form.Control
                type="text"
                value={newValveTypeInput}
                onChange={(e) => setNewValveTypeInput(e.target.value)}
                placeholder="z.B. Presta, Schrader, Dunlop"
                autoFocus
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddValveTypeModal(false)}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleAddNewValveType}>
            Hinzufügen
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Bikes;
